import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, signal, inject, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationService, Notification } from '../../services/notification.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-notification-dropdown',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification-dropdown.component.html',
  styleUrls: ['./notification-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  @ViewChild('dropdownElement', { static: false }) dropdownElement!: ElementRef;

  // Reactive state
  readonly isOpen = signal(false);
  readonly notifications = this.notificationService.notifications;
  readonly unreadCount = this.notificationService.unreadCount;
  readonly hasUnread = this.notificationService.hasUnread;

  // Animation states
  readonly removingNotifications = signal<Set<string>>(new Set());

  ngOnInit(): void {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.onDocumentClick.bind(this));
  }

  toggleDropdown(): void {
    this.isOpen.set(!this.isOpen());
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  markAsRead(notification: Notification, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (!notification.read) {
      this.notificationService.markAsRead(notification.id);
    }

    // Handle actionable notifications
    if (notification.actionable && notification.actions?.length) {
      // For now, execute the first action
      notification.actions[0].action();
    }
  }

  markAllAsRead(): void {
    // Add fade animation to all unread notifications
    const unreadNotifications = this.notifications().filter(n => !n.read);

    unreadNotifications.forEach(notification => {
      const element = document.querySelector(`[data-notification-id="${notification.id}"]`);
      if (element) {
        element.classList.add('marking-read');
      }
    });

    // Mark all as read after animation starts
    setTimeout(() => {
      this.notificationService.markAllAsRead();
    }, 150);
  }

  removeNotification(notificationId: string, event: Event): void {
    event.stopPropagation();

    // Add to removing set for animation
    const removing = new Set(this.removingNotifications());
    removing.add(notificationId);
    this.removingNotifications.set(removing);

    // Remove after animation
    setTimeout(() => {
      this.notificationService.removeNotification(notificationId);
      const newRemoving = new Set(this.removingNotifications());
      newRemoving.delete(notificationId);
      this.removingNotifications.set(newRemoving);
    }, 300);
  }

  executeAction(action: any, event: Event): void {
    event.stopPropagation();
    action.action();
    this.closeDropdown();
  }

  isRemoving(notificationId: string): boolean {
    return this.removingNotifications().has(notificationId);
  }

  getNotificationIcon(type: Notification['type']): string {
    return this.notificationService.getIconForType(type);
  }

  getNotificationColor(type: Notification['type']): string {
    return this.notificationService.getColorForType(type);
  }

  private onDocumentClick(event: Event): void {
    if (this.isOpen() && this.dropdownElement) {
      const target = event.target as HTMLElement;
      const dropdown = this.dropdownElement.nativeElement;

      if (!dropdown.contains(target)) {
        this.closeDropdown();
      }
    }
  }

  // Demo methods for testing
  addDemoNotification(): void {
    const titles = [
      'New Update Available',
      'Task Completed',
      'Meeting Reminder',
      'File Uploaded',
      'Account Verified'
    ];
    const messages = [
      'A new version of the application is available for download.',
      'Your assigned task has been completed successfully.',
      'Don\'t forget about your meeting in 30 minutes.',
      'Your file has been uploaded and is now available.',
      'Your account has been verified and is ready to use.'
    ];

    const randomTitle = titles[Math.floor(Math.random() * titles.length)];
    const randomMessage = messages[Math.floor(Math.random() * messages.length)];

    this.notificationService.addNotification(randomTitle, randomMessage, 'info');
  }

  addProfileNotification(): void {
    this.notificationService.addProfileCompletionNotification(['Phone number', 'Experience', 'Specializations']);
  }

  trackByNotificationId(index: number, notification: Notification): string {
    return notification.id;
  }
}